import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { formatApiError, formatHour, formatINR } from "@/lib/format";
import { toast } from "sonner";
import { CameraOff, CheckCircle2, XCircle, ScanLine } from "lucide-react";

export default function QRScanner({ hubId }) {
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const readerRef = useRef(null);
  const scannerRef = useRef(null);

  const start = async () => {
    setError(null);
    setLastResult(null);
    setScanning(true);
    try {
      const qr = new Html5Qrcode("ml-qr-reader");
      scannerRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decoded) => {
          await qr.pause(true);
          await handleDecoded(decoded);
        },
        () => {}
      );
    } catch (e) {
      setError(e?.message || "Camera unavailable");
      setScanning(false);
    }
  };

  const stop = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch {}
    scannerRef.current = null;
    setScanning(false);
  };

  const handleDecoded = async (text) => {
    try {
      const { data } = await api.post("/admin/scan-qr", {
        token: text,
        hub_id: hubId,
      });
      setLastResult({ ok: true, ...data });
      toast.success(
        data.action === "checked_in"
          ? `Checked in · ${data.booking.user_name}`
          : `Checked out · ${data.booking.user_name}`
      );
    } catch (err) {
      setLastResult({ ok: false, error: formatApiError(err) });
      toast.error(formatApiError(err));
    }
    setTimeout(async () => {
      if (scannerRef.current) {
        try {
          await scannerRef.current.resume();
        } catch {}
      }
    }, 1500);
  };

  const manual = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const tok = fd.get("tok");
    if (!tok) return;
    await handleDecoded(tok);
    e.target.reset();
  };

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="qr-scanner">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#D4B878]/10 text-[#E8D6A3]">
              <ScanLine size={18} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">
                QR check-in
              </div>
              <div className="text-lg font-bold">Scan customer booking</div>
            </div>
          </div>
          <div className="flex gap-2">
            {!scanning ? (
              <Button
                onClick={start}
                data-testid="qr-start-btn"
                className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
              >
                Start camera
              </Button>
            ) : (
              <Button
                onClick={stop}
                data-testid="qr-stop-btn"
                variant="outline"
                className="bg-white/5 border-white/10 rounded-full"
              >
                <CameraOff size={14} className="mr-2" /> Stop
              </Button>
            )}
          </div>
        </div>

        <div
          id="ml-qr-reader"
          ref={readerRef}
          className="rounded-xl overflow-hidden bg-black aspect-video max-w-lg"
          style={{ minHeight: scanning ? 280 : 0 }}
        />
        {!scanning && !error && (
          <div className="text-sm text-zinc-500 mt-4">
            Click <span className="text-[#E8D6A3] font-semibold">Start camera</span> and
            ask the customer to open <span className="text-[#E8D6A3]">My bookings → QR</span>.
            First scan checks in, the next scan checks out.
          </div>
        )}
        {error && <div className="mt-4 text-sm text-red-400">{error}</div>}

        {/* Manual fallback */}
        <form onSubmit={manual} className="mt-5 flex gap-2 max-w-lg" data-testid="qr-manual-form">
          <input
            name="tok"
            placeholder="Paste or type QR value (MIRROR:<booking_id>)"
            className="flex-1 h-10 px-3 rounded-md bg-white/5 border border-white/10 text-white text-sm"
            data-testid="qr-manual-input"
          />
          <Button
            type="submit"
            data-testid="qr-manual-submit"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            Validate
          </Button>
        </form>
      </div>

      {lastResult && (
        <div
          className={`rounded-2xl border p-6 ${
            lastResult.ok
              ? "border-emerald-500/30 bg-emerald-500/5"
              : "border-red-500/30 bg-red-500/5"
          }`}
          data-testid="qr-last-result"
        >
          <div className="flex items-start gap-3">
            {lastResult.ok ? (
              <CheckCircle2 size={28} className="text-emerald-400 shrink-0" />
            ) : (
              <XCircle size={28} className="text-red-400 shrink-0" />
            )}
            <div className="flex-1">
              {lastResult.ok ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge
                      variant="outline"
                      className="border-emerald-500/40 text-emerald-400 capitalize"
                    >
                      {lastResult.action?.replace("_", " ")}
                    </Badge>
                    <span className="text-xs text-zinc-500">
                      {new Date().toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-lg font-bold">
                    {lastResult.booking.user_name}
                  </div>
                  <div className="text-sm text-zinc-400">
                    {lastResult.booking.resource_label} ·{" "}
                    {lastResult.booking.hub_name}
                  </div>
                  <div className="text-sm text-zinc-500">
                    {lastResult.booking.date} ·{" "}
                    {formatHour(lastResult.booking.start_hour)} →{" "}
                    {formatHour(
                      lastResult.booking.start_hour +
                        lastResult.booking.duration_hours
                    )}{" "}
                    · {formatINR(lastResult.booking.price)}
                  </div>
                </>
              ) : (
                <div className="text-red-400 font-semibold">
                  {lastResult.error}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
